import { useRef, useEffect, useCallback, useState } from 'react';
import { useAppDispatch } from '@app/core/hooks/redux-hooks';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';

export interface UseExpansionStateConfig {
  itemIds: string[];
  storedExpandedIds?: Record<string, boolean>;
  saveExpandedIds?: ActionCreatorWithPayload<Record<string, boolean>>;
  focusedItemId?: string | null;
  defaultExpanded?: boolean;
}

const areStatesEqual = (a: Record<string, boolean>, b: Record<string, boolean>): boolean => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) {
    return false;
  }
  return keysA.every((key) => a[key] === b[key]);
};

export const useExpansionState = (config: UseExpansionStateConfig) => {
  const {
    itemIds,
    storedExpandedIds,
    saveExpandedIds,
    focusedItemId,
    defaultExpanded = true,
  } = config;
  const [isInFocusMode, setIsInFocusMode] = useState(Boolean(focusedItemId));
  const itemRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (focusedItemId) {
      setIsInFocusMode(true);
    }
  }, [focusedItemId]);

  useEffect(() => {
    if (!focusedItemId) {
      setIsInFocusMode(false);
    }
  }, [focusedItemId]);

  const buildUnfocusedStates = useCallback((): Record<string, boolean> => {
    const states: Record<string, boolean> = {};
    itemIds.forEach((id) => {
      states[id] = storedExpandedIds?.[id] ?? defaultExpanded;
    });
    return states;
  }, [defaultExpanded, itemIds, storedExpandedIds]);

  const buildInitialStates = useCallback((): Record<string, boolean> => {
    if (isInFocusMode && focusedItemId && itemIds.includes(focusedItemId)) {
      const states: Record<string, boolean> = {};
      itemIds.forEach((id) => {
        states[id] = id === focusedItemId;
      });
      return states;
    }

    return buildUnfocusedStates();
  }, [isInFocusMode, focusedItemId, itemIds, buildUnfocusedStates]);

  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>(buildInitialStates);

  useEffect(() => {
    if (itemIds.length === 0) {
      return;
    }

    if (isInFocusMode && focusedItemId) {
      const focusStates: Record<string, boolean> = {};
      itemIds.forEach((id) => {
        focusStates[id] = id === focusedItemId;
      });

      setExpandedStates((prev) => {
        if (areStatesEqual(prev, focusStates)) {
          return prev;
        }
        return focusStates;
      });
      return;
    }

    const unfocusedStates = buildUnfocusedStates();

    setExpandedStates((prev) => {
      if (areStatesEqual(prev, unfocusedStates)) {
        return prev;
      }
      return unfocusedStates;
    });
  }, [itemIds, isInFocusMode, focusedItemId, buildUnfocusedStates]);

  const handleToggle = useCallback(
    (itemId: string) => {
      setExpandedStates((prev) => {
        const newStates = {
          ...prev,
          [itemId]: !prev[itemId],
        };

        if (!isInFocusMode && saveExpandedIds) {
          dispatch(saveExpandedIds(newStates));
        }

        return newStates;
      });
    },
    [dispatch, isInFocusMode, saveExpandedIds]
  );

  const isExpanded = useCallback(
    (itemId: string) => expandedStates[itemId] ?? true,
    [expandedStates]
  );

  useEffect(() => {
    if (focusedItemId && itemRefs.current[focusedItemId]) {
      itemRefs.current[focusedItemId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [focusedItemId]);

  return {
    itemRefs,
    handleToggle,
    isExpanded,
  };
};
